/**
 * configService.js  —  PostgreSQL-backed API version
 * ────────────────────────────────────────────────────
 * All reads and writes go to the FastAPI backend at API_BASE.
 * Set REACT_APP_API_BASE in your .env (create-react-app) or
 * VITE_API_BASE in your vite.config  to point at the server.
 *
 * Default:  http://localhost:3001
 *
 * The public API surface is identical to the old static-JSON version
 * so Main.jsx / FleetVesselBrowser.jsx need ZERO changes.
 */

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

// ─── Low-level helpers ────────────────────────────────────────────────────────

async function apiGet(path) {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) throw new Error(`GET ${path} → ${res.status}`);
  return res.json();
}

async function apiPost(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = `POST ${path} → ${res.status}`;
    try { const d = await res.json(); msg += `: ${d.detail || JSON.stringify(d)}`; } catch {}
    throw new Error(msg);
  }
  return res.json();
}

async function apiPut(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method:  'PUT',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`PUT ${path} → ${res.status}`);
  return res.json();
}

async function apiDelete(path) {
  const res = await fetch(`${API_BASE}${path}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`DELETE ${path} → ${res.status}`);
  return res.json();
}

// ─── Key helpers (same logic as before — needed by Main.jsx) ──────────────────

export function vesselKey(vesselInfo) {
  if (!vesselInfo) return '';
  const imo = String(vesselInfo.imo || '').replace(/\D/g, '').trim();
  if (imo) return `imo_${imo}`;
  return String(vesselInfo.vessel || '')
    .toLowerCase().trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_-]/g, '');
}

export function fleetKey(fleetOrName) {
  const raw = typeof fleetOrName === 'string'
    ? fleetOrName
    : String((fleetOrName && fleetOrName.companyName) || '');
  return raw.toLowerCase().trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_-]/g, '');
}

// =============================================================================
//  VESSEL CONFIG
// =============================================================================

export async function loadAllVessels() {
  const data = await apiGet('/api/config/vessel');
  const vessels = (data && data.vessels) ? data.vessels : {};
  // Stamp each vessel with its own key so edits always update, never duplicate.
  Object.keys(vessels).forEach(k => { vessels[k]._key = k; });
  return vessels;
}

export async function loadVesselConfig(key) {
  if (!key) return null;
  const all = await loadAllVessels();
  // Stamp _key so any subsequent save uses the original slug, not a re-derived one.
  return all[key] ? { ...all[key], _key: key } : null;
}

export async function saveVesselConfig(vesselInfo) {
  // Preserve the original slug key when editing so the vessel is updated,
  // not duplicated, even if the vessel name or IMO was changed.
  const key = vesselInfo._key || vesselKey(vesselInfo);
  const payload = { ...vesselInfo, _key: key };
  await apiPost(`/api/config/vessel/${encodeURIComponent(key)}`, payload);
  return key;
}

export async function deleteVesselConfig(key) {
  await apiDelete(`/api/config/vessel/${encodeURIComponent(key)}`);
}

export async function deleteVesselAndEngines(key) {
  // Delete engines first (also handled by ON DELETE CASCADE, but explicit is cleaner)
  await apiDelete(`/api/config/engine/${encodeURIComponent(key)}`).catch(() => {});
  await apiDelete(`/api/config/vessel/${encodeURIComponent(key)}`);
}

// ─── Engine ────────────────────────────────────────────────────────────────────

export async function loadAllEngines() {
  const data = await apiGet('/api/config/engine');
  return (data && data.engines) ? data.engines : {};
}

export async function loadEngineConfig(key) {
  if (!key) return [];
  const all = await loadAllEngines();
  return Array.isArray(all[key]) ? all[key] : [];
}

export async function saveEngineConfig(key, engines) {
  await apiPost(`/api/config/engine/${encodeURIComponent(key)}`, engines);
}

export async function deleteEngineConfig(key) {
  await apiDelete(`/api/config/engine/${encodeURIComponent(key)}`);
}

// ─── Fleet ─────────────────────────────────────────────────────────────────────

export async function loadAllFleets() {
  const data = await apiGet('/api/config/fleet');
  const fleets = (data && data.fleets) ? data.fleets : {};
  // Stamp each fleet with its own key so edits always update, never duplicate.
  Object.keys(fleets).forEach(k => { fleets[k]._key = k; });
  return fleets;
}

export async function loadFleetConfig(key) {
  if (!key) return null;
  const all = await loadAllFleets();
  // Stamp _key so any subsequent save uses the original slug, not a re-derived one.
  return all[key] ? { ...all[key], _key: key } : null;
}

export async function saveFleetConfig(fleetInfo) {
  // When editing an existing fleet, _key is stored on the object so we never
  // re-derive the slug from a (possibly changed) companyName and accidentally
  // create a duplicate row.
  const key = fleetInfo._key || fleetKey(fleetInfo);
  const payload = { ...fleetInfo, _key: key };
  await apiPost(`/api/config/fleet/${encodeURIComponent(key)}`, payload);
  return key;
}

export async function deleteFleetConfig(key) {
  await apiDelete(`/api/config/fleet/${encodeURIComponent(key)}`);
}

export async function addVesselToFleet(fKey, vKey) {
  // Load current fleet, add vessel to list, save back
  const fleet = await loadFleetConfig(fKey);
  if (!fleet) return;
  if (!Array.isArray(fleet.vessels)) fleet.vessels = [];
  if (!fleet.vessels.includes(vKey)) {
    fleet.vessels.push(vKey);
    await saveFleetConfig(fleet);
  }
}

export async function removeVesselFromFleet(fKey, vKey) {
  const fleet = await loadFleetConfig(fKey);
  if (!fleet) return;
  fleet.vessels = (fleet.vessels || []).filter(v => v !== vKey);
  await saveFleetConfig(fleet);
}

export async function deleteFleetAndVessels(fKey) {
  // Cascade: vessels → engines via DB FK, but we also remove the fleet record
  await apiDelete(`/api/config/fleet/${encodeURIComponent(fKey)}`);
}

// ─── Combined save / load ──────────────────────────────────────────────────────

export async function saveFullConfig({ info, engines }) {
  const key = await saveVesselConfig(info);
  await saveEngineConfig(key, engines);
  return key;
}

export async function loadFullConfig(key) {
  const [info, engines] = await Promise.all([
    loadVesselConfig(key),
    loadEngineConfig(key),
  ]);
  return { info: info || {}, engines: engines || [] };
}

// ─── Auth ──────────────────────────────────────────────────────────────────────

export async function resolveLogin(username, password) {
  if (!username || !password) return null;
  try {
    const result = await apiPost('/api/auth/login', { username, password });
    return result || null;
  } catch (err) {
    // 401 → invalid credentials (not a server error)
    if (String(err.message).includes('401')) return null;
    throw err;
  }
}

export async function getAccessibleVesselKeys(session) {
  if (!session) return [];
  if (session.role === 'admin') return Object.keys(await loadAllVessels());
  if (session.role === 'fleet') {
    const fleet = await loadFleetConfig(session.fleetKey);
    return fleet ? (fleet.vessels || []) : [];
  }
  if (session.role === 'vessel') return session.vesselKey ? [session.vesselKey] : [];
  return [];
}

export async function getAccessibleFleetKeys(session) {
  if (!session) return [];
  if (session.role === 'admin') return Object.keys(await loadAllFleets());
  if (session.role === 'fleet') return session.fleetKey ? [session.fleetKey] : [];
  return [];
}

// =============================================================================
//  REPORTS  —  persisted in PostgreSQL (replaces old localStorage store)
// =============================================================================

export async function loadReportIndex() {
  const data = await apiGet('/api/config/report');
  return Array.isArray(data.index) ? data.index : [];
}

export async function loadReport(id) {
  if (!id) return null;
  const data = await apiGet('/api/config/report');
  return (data.reports && data.reports[id]) ? data.reports[id] : null;
}

export async function saveReport(snapshot) {
  if (!snapshot || !snapshot.id) return;
  const summary = {
    id:            snapshot.id,
    savedAt:       snapshot.savedAt || new Date().toISOString(),
    vessel:        snapshot.vessel        || '',
    imo:           snapshot.imo           || '',
    vesselKey:     snapshot.vesselKey     || '',
    engineLabel:   snapshot.engineLabel   || '',
    engineType:    snapshot.engineType    || '',
    testLoadPct:   snapshot.testLoadPct   ?? null,
    engineHealth:  snapshot.engineHealth  ?? null,
    findingsCount: snapshot.findingsCount ?? 0,
    criticalCount: snapshot.criticalCount ?? 0,
    warningCount:  snapshot.warningCount  ?? 0,
  };
  await apiPost(`/api/config/report/${encodeURIComponent(snapshot.id)}`, {
    summary,
    snapshot,
  });
}

export async function deleteReport(id) {
  if (!id) return;
  await apiDelete(`/api/config/report/${encodeURIComponent(id)}`);
}

export async function patchReportSummary(id, patch) {
  if (!id) return;
  // Load full snapshot, merge summary fields, save back
  const snap = await loadReport(id);
  if (!snap) return;
  const merged = { ...snap, ...patch };
  await saveReport(merged);
}

// =============================================================================
//  EXPORT / IMPORT helpers
// =============================================================================

function _downloadJson(obj, filename) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function exportAllConfigFiles() {
  const [fleetData, vesselData, engineData] = await Promise.all([
    apiGet('/api/config/fleet'),
    apiGet('/api/config/vessel'),
    apiGet('/api/config/engine'),
  ]);
  _downloadJson(fleetData,  'fleetConfig.json');
  await new Promise(r => setTimeout(r, 300));
  _downloadJson(vesselData, 'vesselConfig.json');
  await new Promise(r => setTimeout(r, 300));
  _downloadJson(engineData, 'engineConfig.json');
}

export async function exportAllConfigFilesWithReports() {
  await exportAllConfigFiles();
  await new Promise(r => setTimeout(r, 300));
  const reportData = await apiGet('/api/config/report');
  _downloadJson(reportData, 'reportConfig.json');
}

/**
 * importFromJsonFiles — uploads JSON file contents to the DB.
 * All four arguments are optional; pass null to skip.
 */
export async function importFromJsonFiles(vesselJson, engineJson, fleetJson, reportJson) {
  const ops = [];
  if (fleetJson?.fleets)   ops.push(apiPut('/api/config/fleet',  fleetJson));
  if (vesselJson?.vessels) ops.push(apiPut('/api/config/vessel', vesselJson));
  if (engineJson?.engines) ops.push(apiPut('/api/config/engine', engineJson));
  if (reportJson)          ops.push(apiPut('/api/config/report', reportJson));
  await Promise.all(ops);

  return {
    fleetsImported:   fleetJson?.fleets   ? Object.keys(fleetJson.fleets).length  : 0,
    vesselsImported:  vesselJson?.vessels ? Object.keys(vesselJson.vessels).length : 0,
    enginesImported:  engineJson?.engines ? Object.keys(engineJson.engines).length : 0,
    reportsImported:  reportJson?.index   ? reportJson.index.length                : 0,
  };
}

// ─── Clear helpers ─────────────────────────────────────────────────────────────

export async function clearVesselConfig()  { await apiPut('/api/config/vessel', { vessels: {} }); }
export async function clearEngineConfig()  { await apiPut('/api/config/engine', { engines: {} }); }
export async function clearFleetConfig()   { await apiPut('/api/config/fleet',  { fleets:  {} }); }
export async function clearReportConfig()  { await apiPut('/api/config/report', { index: [], reports: {} }); }
export async function clearAllConfig() {
  await Promise.all([
    clearVesselConfig(),
    clearEngineConfig(),
    clearFleetConfig(),
    clearReportConfig(),
  ]);
}